/**
 * What the registrar is told about the Drive half of a parish move (cca-vz2o).
 *
 * The reassign and consolidate routes re-home the applicant's Drive folder and
 * answer `drive` (the outcome) plus, for a merge into an existing folder,
 * `driveDetail` (counts and ids only). Before cca-vz2o the reassign screen
 * ignored `drive` entirely and consolidate showed only the two good outcomes —
 * so a Drive failure was silent (OBS-9). This turns every outcome into one
 * sentence, flags the ones that need the registrar to act, and links the kept
 * old folder when something is left in it. When the record's remembered-folder
 * link could not be saved — the response's TOP-LEVEL `folderIdSaved: false`,
 * on any outcome (LeC r2) — that is said too; the Drive outcome itself is
 * reported unchanged. Pure: no React, no fetch.
 */

/** The server's `driveDetail` (packages/cca-parishes-worker drive/watcher.ts MergeDetail). */
export interface DriveMoveDetail {
  moved: number;
  notMoved: number;
  sourceFolderId: string;
  sourceTrashed: boolean;
}

export interface DriveMoveNote {
  /** The sentence to show after the move's own confirmation. */
  text: string;
  /** True when something in Drive needs the registrar's attention. */
  warn: boolean;
  /** The kept old folder, when something is still in it; otherwise null. */
  folderUrl: string | null;
}

/** Appended when the record's remembered-folder write failed. */
export const FOLDER_LINK_NOT_SAVED =
  "The record's link to its Drive folder wasn't saved, so the portal may not find the folder " +
  'next time. Use Report a problem (bottom right) so it can be put right.';

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function oldFolderUrl(detail?: DriveMoveDetail | null): string | null {
  return detail?.sourceFolderId
    ? `https://drive.google.com/drive/folders/${encodeURIComponent(detail.sourceFolderId)}`
    : null;
}

function outcomeNote(
  drive: string | null | undefined,
  detail?: DriveMoveDetail | null,
): DriveMoveNote | null {
  switch (drive) {
    case 'reparented':
      return { text: 'Drive folder moved.', warn: false, folderUrl: null };
    case 'merged':
      if (detail?.sourceTrashed === false) {
        return {
          text:
            'Drive files merged into the existing folder. The old folder was kept ' +
            '(it should now be empty) — delete it in Drive.',
          warn: true,
          folderUrl: oldFolderUrl(detail),
        };
      }
      return { text: 'Drive folder merged and duplicate removed.', warn: false, folderUrl: null };
    case 'partial': {
      if (!detail) {
        return {
          text:
            'Some Drive files could not be moved and are still in the old folder, ' +
            'which has been kept — move them in Drive.',
          warn: true,
          folderUrl: null,
        };
      }
      const { moved, notMoved } = detail;
      const total = moved + notMoved;
      return {
        text:
          `${moved} of ${total} ${plural(total, 'file', 'files')} moved into the existing folder. ` +
          `${notMoved} could not be moved and ${plural(notMoved, 'is', 'are')} still in the old folder, ` +
          `which has been kept — move ${plural(notMoved, 'it', 'them')} in Drive.`,
        warn: true,
        folderUrl: oldFolderUrl(detail),
      };
    }
    case 'failed':
      return {
        text: 'The record moved, but its Drive folder did not — move the folder by hand in Drive.',
        warn: true,
        folderUrl: null,
      };
    default:
      return null;
  }
}

/**
 * The note for a Drive outcome, or null when there is nothing to say (no
 * folder to move, already in place, or an outcome these routes cannot produce).
 * `folderIdSaved` is the response's top-level flag: only `false` adds the
 * unsaved-link sentence (absent means the write succeeded or never ran).
 */
export function driveMoveNote(
  drive: string | null | undefined,
  detail?: DriveMoveDetail | null,
  folderIdSaved?: boolean,
): DriveMoveNote | null {
  const note = outcomeNote(drive, detail);
  if (folderIdSaved !== false) return note;
  // The Drive outcome stands; the failed D1 write is said on its own.
  return note
    ? { ...note, text: `${note.text} ${FOLDER_LINK_NOT_SAVED}`, warn: true }
    : { text: FOLDER_LINK_NOT_SAVED, warn: true, folderUrl: null };
}
